/**
 * BrainFogCard — мозъчната мъгла срещу инсулиновото натоварване (DIL),
 * съня и резултата от когнитивните тестове.
 * Данни: GET /api/pain-diary/analysis (полето fog)
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Brain, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const R = ({ label, r, isDark }) => {
  const color = r == null ? "opacity-50"
    : Math.abs(r) >= 0.5 ? "text-rose-500"
    : Math.abs(r) >= 0.3 ? "text-amber-500" : "opacity-60";
  return (
    <div className={`p-2 rounded-lg ${isDark ? "bg-white/5" : "bg-slate-50"}`}>
      <div className="text-[9px] uppercase font-bold opacity-60">{label}</div>
      <div className={`text-base font-bold ${color}`}>{r != null ? `r=${r}` : "—"}</div>
    </div>
  );
};

export default function BrainFogCard({ isDark = false, cardCls = "" }) {
  const [d, setD] = useState(null);
  const [fog, setFog] = useState(4);
  const [busy, setBusy] = useState(false);
  const border = isDark ? "border-white/10" : "border-gray-200";

  const load = () => axios.get(`${API}/pain-diary/analysis`)
    .then((r) => setD(r.data.fog || null)).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!d) return null;

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/pain-diary/log`, { intensity: 0, brain_fog: fog });
      setD(data.analysis.fog);
      toast.success("Отбелязана мъгла");
    } catch { toast.error("Не се записа"); }
    finally { setBusy(false); }
  };

  return (
    <div className={cardCls || `p-4 rounded-2xl border ${border}`} data-testid="brain-fog-card">
      <div className="text-sm font-bold inline-flex items-center gap-1.5">
        <Brain className="w-4 h-4 text-sky-500" /> Мозъчна мъгла
      </div>
      <p className="text-[11px] opacity-75 mt-0.5">
        Отбелязвайте мъглата и я сверяваме с инсулиновото натоварване на
        храненията (DIL), със съня и с резултатите от тестовете.
        {d.days_logged ? ` Досега ${d.days_logged} дни, средно ${d.avg}/10.` : ""}
      </p>

      <label className="text-[11px] font-semibold opacity-80 block mt-2">
        Днес: <span className="tabular-nums" data-testid="fog-value">{fog}</span>/10
        <input type="range" min="0" max="10" value={fog}
          onChange={(e) => setFog(Number(e.target.value))}
          className="w-full mt-1 accent-sky-600" data-testid="fog-slider" />
      </label>
      <button type="button" onClick={save} disabled={busy}
        className="mt-1 px-3 py-1.5 rounded-lg bg-sky-600 text-white text-[12px] font-semibold inline-flex items-center gap-1.5 disabled:opacity-60"
        data-testid="fog-save">
        {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Отбележи
      </button>

      <div className="mt-3 grid grid-cols-3 gap-2" data-testid="fog-correlations">
        <R label="Мъгла ↔ DIL" r={d.vs_dil_r} isDark={isDark} />
        <R label="Мъгла ↔ сън" r={d.vs_sleep_r} isDark={isDark} />
        <R label="Мъгла ↔ тест" r={d.vs_cognitive_r} isDark={isDark} />
      </div>

      {d.links?.map((l, i) => (
        <div key={i} className={`mt-2 p-2 rounded-lg border ${border}`}
          data-testid={`fog-insight-${l.twin}`}>
          <div className="text-[11px] font-bold">{l.title_bg}</div>
          <div className="text-[10px] opacity-85">{l.why_bg}</div>
          <div className="text-[10px] font-medium mt-0.5">{l.action_bg}</div>
          <Link to={l.route}
            className="text-[10px] font-bold text-sky-600 dark:text-sky-300 inline-flex items-center gap-1 mt-0.5"
            data-testid={`fog-insight-cta-${l.twin}`}>
            Отвори {l.twin_bg} <ArrowRight className="w-3 h-3" />
          </Link>
        </div>
      ))}
      <p className="text-[9px] opacity-50 mt-2">{d.note_bg}</p>
    </div>
  );
}
